/**
 * API klienti — docs/API.md.
 *
 * Tiplar `@voice/api-client` dan keladi va ular GENERATSIYA QILINADI (D-013).
 * Bu yerda faqat transport: token, xato shakli, qayta urinish.
 */

import type { ErrorResponse } from '@voice/api-client';

const BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8000';

/**
 * So'rov chegarasi.
 *
 * Timeout'siz `fetch` mobil tarmoqda **abadiy** osilib qolishi mumkin:
 * TCP ochilgan, javob yo'q, xato ham yo'q. Klient uchun bu eng yomon
 * holat — ekranda "Yuklanmoqda…" turadi va foydalanuvchi qayta urinish
 * tugmasini ham ko'rmaydi, chunki hech narsa yiqilmagan.
 *
 * 15 soniya barcha endpointlarga yetadi: uzoq ishlaydigan yo'l yo'q,
 * real vaqt esa WS orqali ketadi (`RealtimeProvider`).
 */
const TIMEOUT_MS = 15_000;

/** Server qaytargan xato — `code` bo'yicha shart yoziladi, `message` bo'yicha emas. */
export class ApiError extends Error {
  constructor(
    /**
     * Odatda `ErrorCode` dagi qiymatlardan biri (docs/API.md §13), lekin
     * tip `string`: server yangi kod qo'shsa eski klient uni baribir
     * qabul qilishi kerak. `ErrorCode | string` yozish ma'nosiz — TS uni
     * `string` ga yig'ib yuboradi.
     */
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth && accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  // `exactOptionalPropertyTypes` yoqilgan — `body: undefined` yozib
  // bo'lmaydi, maydonni umuman qo'shmaslik kerak.
  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  init.signal = controller.signal;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch (err) {
    // Tarmoq uzildi yoki timeout. Ikkalasi ham `ApiError` ga aylanadi —
    // chaqiruvchi bitta shakl bilan ishlaydi va "xato holati" ekranga
    // chiqadi. Ilgari bu yerda hech narsa yo'q edi va `fetch` osilib
    // qolsa ilova jim qotardi.
    throw new ApiError(
      controller.signal.aborted ? 'TIMEOUT' : 'NETWORK_ERROR',
      controller.signal.aborted ? 'Server javob bermadi' : 'Tarmoqqa ulanib bo‘lmadi',
      0,
      { cause: err instanceof Error ? err.message : String(err) },
    );
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let code = 'INTERNAL_ERROR';
    let message = 'Kutilmagan xato yuz berdi';
    let details: Record<string, unknown> | undefined;

    try {
      const payload = (await response.json()) as ErrorResponse;
      code = payload.error.code;
      message = payload.error.message;
      details = payload.error.details ?? undefined;
    } catch {
      // Javob JSON emas (masalan nginx 502) — standart matn qoladi.
    }
    throw new ApiError(code, message, response.status, details);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
